import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Grid,
  useTheme,
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { useChartTheme } from '../../themes/chartTheme';
import { tokens } from '../../theme';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import fetchData from '../../helpers/fetchData';
import { formatNumber } from '../../helpers/numberUtils';
import { exportToExcel } from '../../helpers/exportFuncs';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import { getComparator, Order, sortedRowInformation } from '../../helpers/sortingsFuncs';
import LoadingSpinner from '../LoadingSpinner';
import { visuallyHidden } from '@mui/utils';

interface HeaderMappingItem {
  name: string;
  type: string;
  digits: number;
  attach: string;
}

interface RoiPerMarketCapData {
  market_cap_range: string;
  newsletter: string;
  leads: number;
  revenue_30d: number;
  total_cost: number;
  roi_30d: number;
}

// The header mapping for the table
const headerMapping: Record<string, HeaderMappingItem> = {
  market_cap_range: {
    name: 'Market Cap Range',
    type: 'string',
    digits: 0,
    attach: '',
  },
  newsletter: {
    name: 'Newsletter',
    type: 'string',
    digits: 0,
    attach: '',
  },
  leads: {
    name: 'Leads (DOI)',
    type: 'number',
    digits: 0,
    attach: '',
  },
  revenue_30d: {
    name: 'Revenue 30d',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  total_cost: {
    name: 'Costs',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  roi_30d: {
    name: 'ROI 30d',
    type: 'number',
    digits: 2,
    attach: '%',
  },
};

const RoiPerMarketCap: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const [data, setData] = useState<RoiPerMarketCapData[]>([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof RoiPerMarketCapData>('leads');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof RoiPerMarketCapData,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const fetchRoiData = async (filterState: IFilterState): Promise<RoiPerMarketCapData[]> => {
    if (!filterState.startDate || !filterState.endDate || !filterState.network) {
      return [];
    }

    const networks = Array.isArray(filterState.network)
      ? filterState.network
      : [filterState.network];
    const themes = Array.isArray(filterState.theme)
      ? filterState.theme
      : filterState.theme
        ? [filterState.theme]
        : [];
    const params = {
      start_date: dateToSqlDate(filterState.startDate),
      end_date: dateToSqlDate(filterState.endDate),
      network: networks,
      ...(themes.length > 0 && { theme: themes }),
      ...(filterState.vnrVerlag.length > 0 && { verlag: filterState.vnrVerlag }),
    };

    return fetchData<RoiPerMarketCapData>('/api/vnr/roi-per-market-cap', params) as Promise<
      RoiPerMarketCapData[]
    >;
  };

  useEffect(() => {
    const loadData = async () => {
      const hasNetwork = Array.isArray(filterState.network)
        ? filterState.network.length > 0
        : !!filterState.network;

      if (!hasNetwork || !filterState.startDate || !filterState.endDate) {
        return;
      }

      setLoading(true);
      try {
        // Fetch both market cap and asset type data
        const [marketCapResult, assetTypeResult] = await Promise.all([
          fetchRoiData(filterState),
          fetchAssetTypeData(filterState),
        ]);

        const processedMarketCapResult = marketCapResult.map((item) => ({
          ...item,
          leads: parseInt(String(item.leads), 10) || 0,
          revenue_30d: parseFloat(String(item.revenue_30d)) || 0,
          total_cost: parseFloat(String(item.total_cost)) || 0,
          roi_30d: parseFloat(String(item.roi_30d)) || 0,
        }));

        const processedAssetTypeResult = assetTypeResult.map((item) => ({
          ...item,
          leads: parseInt(String(item.leads), 10) || 0,
          revenue_30d: parseFloat(String(item.revenue_30d)) || 0,
          total_cost: parseFloat(String(item.total_cost)) || 0,
          roi_30d: parseFloat(String(item.roi_30d)) || 0,
        }));

        setData(processedMarketCapResult);
        setAssetTypeData(processedAssetTypeResult);
      } catch (error) {
        console.error('Error fetching ROI data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    filterState.network,
    filterState.theme,
    filterState.vnrVerlag,
    filterState.startDate,
    filterState.endDate,
  ]);

  const computeSummary = (data: RoiPerMarketCapData[]): RoiPerMarketCapData => {
    if (!data.length) {
      return {
        market_cap_range: 'Insgesamt',
        newsletter: '',
        leads: 0,
        revenue_30d: 0,
        total_cost: 0,
        roi_30d: 0,
      };
    }

    const summary = {
      market_cap_range: 'Insgesamt',
      newsletter: '',
      leads: 0,
      revenue_30d: 0,
      total_cost: 0,
      roi_30d: 0,
    };

    data.forEach((item) => {
      summary.leads += item.leads || 0;
      summary.revenue_30d += item.revenue_30d || 0;
      summary.total_cost += item.total_cost || 0;
    });

    // Calculate overall ROI
    summary.roi_30d = summary.total_cost > 0 ? (summary.revenue_30d / summary.total_cost) * 100 : 0;

    return summary;
  };

  const handleExport = () => {
    const exportData = data.map((item) => ({
      'Market Cap Range': item.market_cap_range,
      Newsletter: item.newsletter,
      'Leads (DOI)': item.leads,
      'Revenue 30d': item.revenue_30d,
      Costs: item.total_cost,
      'ROI 30d (%)': item.roi_30d,
    }));
    exportToExcel(exportData, 'ROI_per_Market_Cap');
  };

  const prepareChartData = () => {
    const aggregated = data.reduce(
      (acc, item) => {
        if (!acc[item.market_cap_range]) {
          acc[item.market_cap_range] = {
            market_cap_range: item.market_cap_range,
            leads: 0,
            revenue_30d: 0,
            total_cost: 0,
            roi_30d: 0,
          };
        }
        acc[item.market_cap_range].leads += item.leads;
        acc[item.market_cap_range].revenue_30d += item.revenue_30d;
        acc[item.market_cap_range].total_cost += item.total_cost;
        return acc;
      },
      {} as Record<string, any>,
    );

    // Calculate aggregated ROI
    Object.values(aggregated).forEach((item: any) => {
      item.roi_30d = item.total_cost > 0 ? (item.revenue_30d / item.total_cost) * 100 : 0;
    });

    return Object.values(aggregated);
  };

  const prepareVtPtRoiData = () => {
    // Filter for VT/PT newsletters only
    const vtPtData = data.filter((item) => item.newsletter === 'VT' || item.newsletter === 'PT');

    // Group by market_cap_range and newsletter
    const grouped = vtPtData.reduce(
      (acc, item) => {
        const key = item.market_cap_range;
        if (!acc[key]) {
          acc[key] = {
            market_cap_range: item.market_cap_range,
            VT: 0,
            PT: 0,
          };
        }

        // Calculate ROI for this specific item and round to 2 decimal places
        const roi = item.total_cost > 0 ? (item.revenue_30d / item.total_cost) * 100 : 0;
        acc[key][item.newsletter as 'VT' | 'PT'] = Math.round(roi * 100) / 100;

        return acc;
      },
      {} as Record<string, { market_cap_range: string; VT: number; PT: number }>,
    );

    return Object.values(grouped);
  };

  const [assetTypeData, setAssetTypeData] = useState<any[]>([]);

  const fetchAssetTypeData = async (filterState: IFilterState): Promise<any[]> => {
    if (!filterState.startDate || !filterState.endDate || !filterState.network) {
      return [];
    }

    const networks = Array.isArray(filterState.network)
      ? filterState.network
      : [filterState.network];
    const themes = Array.isArray(filterState.theme)
      ? filterState.theme
      : filterState.theme
        ? [filterState.theme]
        : [];
    const params = {
      start_date: dateToSqlDate(filterState.startDate),
      end_date: dateToSqlDate(filterState.endDate),
      network: networks,
      ...(themes.length > 0 && { theme: themes }),
      ...(filterState.vnrVerlag.length > 0 && { verlag: filterState.vnrVerlag }),
    };

    return fetchData<any>('/api/vnr/roi-per-asset-type', params) as Promise<any[]>;
  };

  const prepareAssetTypeVtPtData = (): { asset_type: string; VT: number; PT: number }[] => {
    // Group by asset_type and newsletter
    const grouped = assetTypeData.reduce(
      (acc, item) => {
        const key = item.asset_type || 'Unknown';
        if (!acc[key]) {
          acc[key] = {
            asset_type: key,
            VT: 0,
            PT: 0,
          };
        }

        // Use the ROI directly from the database calculation and round to 2 decimal places
        const roi = parseFloat(String(item.roi_30d)) || 0;
        acc[key][item.newsletter as 'VT' | 'PT'] = Math.round(roi * 100) / 100;

        return acc;
      },
      {} as Record<string, { asset_type: string; VT: number; PT: number }>,
    );

    return Object.values(grouped);
  };

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();

  if (loading) {
    return <LoadingSpinner />;
  }

  const sortedData = sortedRowInformation(data, getComparator(order, orderBy));
  const summaryData = computeSummary(data);
  const chartData = prepareChartData();
  const vtPtRoiData = prepareVtPtRoiData();
  const assetTypeVtPtData = prepareAssetTypeVtPtData();

  return (
    <Box sx={{ margin: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <h2 style={{ margin: 0 }}>ROI nach Market Cap (Einzel-Analysen-Leads)</h2>
        <Button
          variant="contained"
          color="primary"
          onClick={handleExport}
          disabled={data.length === 0}
        >
          Export to Excel
        </Button>
      </Box>

      {data.length === 0 ? (
        <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
          <p>No market cap ROI data available for the selected period and filters.</p>
        </Box>
      ) : (
        <>
          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Leads & Revenue Chart */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: 400 }}>
                <Box sx={{ mb: 1, textAlign: 'center' }}>
                  <h4 style={{ margin: 0 }}>Leads & Revenue by Market Cap</h4>
                </Box>
                <Box sx={{ height: 320 }}>
                  <ResponsiveBar
                    data={chartData}
                    keys={['leads', 'revenue_30d']}
                    indexBy="market_cap_range"
                    theme={chartTheme}
                    margin={{ top: 20, right: 80, bottom: 60, left: 80 }}
                    padding={0.3}
                    groupMode="grouped"
                    colors={[colors.blueAccent[500], colors.greenAccent[500]]}
                    borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    axisTop={null}
                    axisRight={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'Revenue (€)',
                      legendPosition: 'middle',
                      legendOffset: 60,
                    }}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: 'Market Cap Range',
                      legendPosition: 'middle',
                      legendOffset: 50,
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'Leads',
                      legendPosition: 'middle',
                      legendOffset: -60,
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    legends={[
                      {
                        dataFrom: 'keys',
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 120,
                        translateY: 0,
                        itemsSpacing: 2,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemDirection: 'left-to-right',
                        itemOpacity: 0.85,
                        symbolSize: 20,
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemOpacity: 1,
                            },
                          },
                        ],
                      },
                    ]}
                    animate={true}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* ROI Comparison Chart */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: 400 }}>
                <Box sx={{ mb: 1, textAlign: 'center' }}>
                  <h4 style={{ margin: 0 }}>ROI Comparison by Market Cap VT/PT NL</h4>
                </Box>
                <Box sx={{ height: 320 }}>
                  <ResponsiveBar
                    data={vtPtRoiData}
                    keys={['VT', 'PT']}
                    indexBy="market_cap_range"
                    theme={chartTheme}
                    margin={{ top: 20, right: 80, bottom: 60, left: 80 }}
                    padding={0.3}
                    groupMode="grouped"
                    colors={[colors.redAccent[500], colors.blueAccent[500]]}
                    borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: 'Market Cap Range',
                      legendPosition: 'middle',
                      legendOffset: 50,
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'ROI 30d (%)',
                      legendPosition: 'middle',
                      legendOffset: -60,
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    labelFormat={(value) => `${formatNumber(Number(value), 2)}%`}
                    legends={[
                      {
                        dataFrom: 'keys',
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 120,
                        translateY: 0,
                        itemsSpacing: 2,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemDirection: 'left-to-right',
                        itemOpacity: 0.85,
                        symbolSize: 20,
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemOpacity: 1,
                            },
                          },
                        ],
                      },
                    ]}
                    animate={true}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Asset Type ROI Comparison Chart */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: 400 }}>
                <Box sx={{ mb: 1, textAlign: 'center' }}>
                  <h4 style={{ margin: 0 }}>ROI by Asset Type VT/PT NL</h4>
                </Box>
                <Box sx={{ height: 320 }}>
                  <ResponsiveBar
                    data={assetTypeVtPtData}
                    keys={['VT', 'PT']}
                    indexBy="asset_type"
                    theme={chartTheme}
                    margin={{ top: 20, right: 80, bottom: 80, left: 80 }}
                    padding={0.3}
                    groupMode="grouped"
                    colors={[colors.redAccent[500], colors.blueAccent[500]]}
                    borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: 'Asset Type',
                      legendPosition: 'middle',
                      legendOffset: 60,
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'ROI 30d (%)',
                      legendPosition: 'middle',
                      legendOffset: -60,
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    labelFormat={(value) => `${formatNumber(Number(value), 2)}%`}
                    legends={[
                      {
                        dataFrom: 'keys',
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 120,
                        translateY: 0,
                        itemsSpacing: 2,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemDirection: 'left-to-right',
                        itemOpacity: 0.85,
                        symbolSize: 20,
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemOpacity: 1,
                            },
                          },
                        ],
                      },
                    ]}
                    animate={true}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Table Section */}
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
              <TableHead>
                <TableRow>
                  {Object.keys(headerMapping).map((key) => (
                    <TableCell
                      key={key}
                      align={headerMapping[key].type === 'number' ? 'right' : 'left'}
                      sortDirection={orderBy === key ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === key}
                        direction={orderBy === key ? order : 'asc'}
                        onClick={(event) =>
                          handleRequestSort(event, key as keyof RoiPerMarketCapData)
                        }
                      >
                        {headerMapping[key].name}
                        {orderBy === key ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((row, index) => (
                  <TableRow
                    hover
                    key={`${row.market_cap_range}-${row.newsletter}-${index}`}
                    tabIndex={-1}
                  >
                    <TableCell component="th" scope="row">
                      {row.market_cap_range}
                    </TableCell>
                    <TableCell>{row.newsletter}</TableCell>
                    <TableCell align="right">
                      {formatNumber(row.leads, headerMapping.leads.digits)}
                      {headerMapping.leads.attach}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.revenue_30d, headerMapping.revenue_30d.digits)}
                      {headerMapping.revenue_30d.attach}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.total_cost, headerMapping.total_cost.digits)}
                      {headerMapping.total_cost.attach}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.roi_30d, headerMapping.roi_30d.digits)}
                      {headerMapping.roi_30d.attach}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Summary Row */}
                <TableRow sx={{ backgroundColor: 'action.hover', fontWeight: 'bold' }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    {summaryData.market_cap_range}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{summaryData.newsletter}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(summaryData.leads, headerMapping.leads.digits)}
                    {headerMapping.leads.attach}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(summaryData.revenue_30d, headerMapping.revenue_30d.digits)}
                    {headerMapping.revenue_30d.attach}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(summaryData.total_cost, headerMapping.total_cost.digits)}
                    {headerMapping.total_cost.attach}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(summaryData.roi_30d, headerMapping.roi_30d.digits)}
                    {headerMapping.roi_30d.attach}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default RoiPerMarketCap;
